import { newIdempotencyKey, request } from "./transport";

export type CodexAuthSessionStatus =
  | "queued"
  | "starting"
  | "awaiting_user"
  | "cancel_requested"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "expired";

export interface CodexCredential {
  id: string;
  label: string;
  auth_mode: "chatgpt_device";
  status: "active" | "superseded" | "revoked";
  created_by_id: string | null;
  codex_cli_version: string | null;
  version: number;
  last_validated_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CodexAuthSession {
  id: string;
  status: CodexAuthSessionStatus;
  requested_by_id: string | null;
  credential_id: string | null;
  version: number;
  verification_uri: string | null;
  user_code: string | null;
  code_expires_at: string | null;
  requested_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  cancellation_requested_at: string | null;
  error_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface CodexAuthStatus {
  ready: boolean;
  credential: CodexCredential | null;
  session: CodexAuthSession | null;
}

export async function status(): Promise<CodexAuthStatus> {
  return (await request<CodexAuthStatus>("GET", "/api/v1/admin/codex-auth")).body;
}

export async function start(): Promise<CodexAuthSession> {
  return (
    await request<CodexAuthSession>(
      "POST",
      "/api/v1/admin/codex-auth/device-sessions",
      {},
      { idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function get(sessionId: string): Promise<CodexAuthSession> {
  return (
    await request<CodexAuthSession>(
      "GET",
      `/api/v1/admin/codex-auth/device-sessions/${sessionId}`,
    )
  ).body;
}

export async function cancel(session: CodexAuthSession): Promise<CodexAuthSession> {
  return (
    await request<CodexAuthSession>(
      "POST",
      `/api/v1/admin/codex-auth/device-sessions/${session.id}/cancel`,
      undefined,
      { etag: session.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function revoke(credential: CodexCredential): Promise<CodexCredential> {
  return (
    await request<CodexCredential>(
      "POST",
      `/api/v1/admin/codex-auth/credentials/${credential.id}/revoke`,
      undefined,
      { etag: credential.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}
